// "Whose runs?" — the screen that is not an empty list (#231), a list of names
// rather than a box for an id (#414), and now the screen an invitee can act on
// (#440).
//
// A session may be perfectly valid and still be reading as nobody: `workspace_id`
// and `org_id` are the session's selected context and both start null
// (`routes/auth.py::session`). Rendering that as a run list with no rows in it
// would tell a person their workspace has no runs, which is a different and
// false statement. So this screen exists, and the difference is what it says.
//
// **It offers the memberships by name** — `GET /auth/memberships` lists them,
// under no context, which is the read #231 had no route for. A session with
// exactly one is selected without being asked (`useConsole.ts`), so this screen
// is seen by people who hold several, and by people who hold none.
//
// **And it offers the invitations waiting for them** (#440). This is where an
// invitee lands, and on 2026-09-12 it explained the situation and offered
// nothing: the only route into the workspace was a link in an email, and the one
// action on the screen was the wrong one — create a workspace of your own. So the
// screen now has three states rather than two, and which one it is in is decided
// by what is actually waiting:
//
//   - invitations waiting -> that is the content, and the heading says so;
//   - none, and the address is unverified -> the verification is the next step,
//     because an unverified address is shown no invitation at all
//     (`0026_invites_waiting.sql`), and saying nothing here would make a waiting
//     invitation look like an absent one;
//   - none, and verified -> nobody has asked for them yet, which is the truth
//     and is what the screen said before.
//
// **A platform admin is offered every organisation** (#701). A superadmin with
// no membership was told "You are not in a workspace yet" and had nowhere to go,
// though the API lets it enter any context. So for `user.superadmin` alone the
// screen carries the platform's chooser (`PlatformEntry.tsx`); the routes behind
// it refuse anyone else, and the check here only keeps it off their screen.

import type { OrgRow } from "../api/customers";
import type { WaitingInvitation } from "../api/invites";
import { choiceFor, orgKindName, type Membership } from "../api/memberships";
import type { ContextChoice as Choice, PublicUser } from "../api/session";
import { Centre, initialOf, Notice } from "./chrome";
import { ResendVerification } from "./Doors";
import { PlatformEntry, type Enter, type ListWorkspaces } from "./PlatformEntry";
import { WaitingInvitations } from "./WaitingInvitations";

export function ContextChoice({
  user,
  memberships,
  error,
  waiting,
  waitingError,
  onChoose,
  onAccept,
  onResend,
  onRetry,
  onSignOut,
  platform,
}: {
  user: PublicUser;
  /** The list, or null while it is being read. */
  memberships: Membership[] | null;
  /** A failure reading the list, or selecting from it. */
  error: string | null;
  /** The invitations addressed to this user, or null while being read (#440). */
  waiting: WaitingInvitation[] | null;
  waitingError: string | null;
  onChoose: (choice: Choice) => Promise<void>;
  onAccept: (inviteId: string) => Promise<void>;
  onResend: () => Promise<void>;
  onRetry: () => void;
  onSignOut: () => void;
  /** The platform admin's chooser (#701); shown to a superadmin and to nobody else. */
  platform?: {
    orgs: OrgRow[] | null;
    error: string | null;
    listWorkspaces: ListWorkspaces;
    onEnter: Enter;
  };
}) {
  const invitations = waiting ?? [];
  const admin = user.superadmin && platform !== undefined;
  const invited = invitations.length > 0;
  const none = memberships !== null && memberships.length === 0;
  // Only once both reads have landed: an invitee whose list is still in flight
  // must not be told for half a second that nobody has invited them.
  const settled = memberships !== null && waiting !== null;
  const nothingWaiting = none && settled && !invited;
  return (
    <Centre
      title={titleOf({ invited, none, admin })}
      lede={ledeOf({ invited, none, nothingWaiting, verified: user.email_verified, admin })}
      // An invitation card carries two addresses and a role; at a sign-in form's
      // width it wraps all of them. The picker and the empty states keep it; the
      // platform's chooser, a list of every organisation, takes the width too.
      wide={invited || admin}
    >
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {!settled && error === null && waitingError === null ? (
        <p className="muted" role="status">
          Reading your workspaces…
        </p>
      ) : null}
      {memberships !== null && memberships.length > 0 ? (
        <ul className="choices" aria-label="Your workspaces">
          {memberships.map((membership) => (
            <li key={`${membership.kind}:${membership.workspace_id ?? membership.org_id}`}>
              <button
                type="button"
                className="choice"
                onClick={() => void onChoose(choiceFor(membership)).catch(() => {
                  // The context transition presents the refusal beside these choices.
                })}
              >
                <span className="initial" aria-hidden="true">
                  {initialOf(membership.name ?? "?")}
                </span>
                <span>
                  <strong>{membership.name ?? membership.workspace_id ?? membership.org_id}</strong>
                  <small>
                    {membership.kind === "workspace"
                      ? membership.org_name && membership.org_name !== membership.name
                        ? `Workspace · ${membership.org_name}`
                        : "Workspace"
                      : orgKindName(membership.org_kind)}
                  </small>
                </span>
                <span className="tag choice__role">{membership.role}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <WaitingInvitations
        invitations={invitations}
        address={user.email}
        error={waitingError}
        onAccept={onAccept}
        shape="full"
      />
      {admin && platform !== undefined ? (
        <PlatformEntry
          orgs={platform.orgs}
          error={platform.error}
          listWorkspaces={platform.listWorkspaces}
          onEnter={platform.onEnter}
        />
      ) : null}
      {/* An unverified address is shown no invitation, by design. Said here
          rather than left as an empty screen, because the person cannot tell
          those two situations apart and the action that fixes one is right here. */}
      {nothingWaiting && !user.email_verified ? <ResendVerification onResend={onResend} /> : null}
      <div className="actions">
        {error !== null || waitingError !== null || none ? (
          <button type="button" className="btn" onClick={onRetry}>
            Check again
          </button>
        ) : null}
        <button type="button" className="link link--muted" onClick={onSignOut}>
          Sign out ({user.email})
        </button>
      </div>
    </Centre>
  );
}

function titleOf({
  invited,
  none,
  admin,
}: {
  invited: boolean;
  none: boolean;
  admin: boolean;
}): string {
  if (invited) return "You have been invited";
  if (none && admin) return "Choose where to enter";
  if (none) return "You are not in a workspace yet";
  return "Choose a workspace";
}

function ledeOf({
  invited,
  none,
  nothingWaiting,
  verified,
  admin,
}: {
  invited: boolean;
  none: boolean;
  nothingWaiting: boolean;
  verified: boolean;
  admin: boolean;
}): string {
  if (invited) {
    return none
      ? "Accept it and you are in. Nothing else is needed — no link to find, and no workspace of your own to create."
      : "You are in the workspaces above, and these have asked for you as well.";
  }
  if (none && admin) {
    return "You hold no membership. As a platform admin you may enter any partner, customer or workspace below.";
  }
  if (nothingWaiting && !verified) {
    return "Torque Assist is invitation-only, and an invitation is only shown to a verified address. Verify yours and any invitation waiting for it will appear here.";
  }
  if (none) {
    return "Torque Assist is invitation-only for now. When a colleague or the platform invites you, it will show up here — and the link in that email brings you in too.";
  }
  return "Choose where to work. Each workspace keeps its investigations, accounts and permissions separate.";
}
